import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, LogOut, Info } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getProjectWithRfi } from '@/lib/api/projects';
import { TeamAvatars, type TeamMember } from '@/components/TeamAvatars';
import diiLogo from '@/assets/dii-logo.png';

interface AppHeaderProps {
  showBack?: boolean;
  backTo?: string;
  syncStatus?: React.ReactNode;
  completedSteps?: string[];
  title?: React.ReactNode;
  teamMembers?: TeamMember[];
  onToggleTeam?: () => void;
  onMembersChange?: (members: TeamMember[]) => void;
  canManageTeam?: boolean;
}

const ALL_NAV_ITEMS: { label: string; path: string; step: string }[] = [];

const getNavItems = (isHomePage: boolean, completedSteps?: string[]) => {
  // If completedSteps is provided, only show completed steps
  if (completedSteps !== undefined) {
    return ALL_NAV_ITEMS.filter(item => completedSteps.includes(item.step));
  }
  // Default behavior: show all items; home-page filtering (if needed) is handled elsewhere.
  return ALL_NAV_ITEMS;
};

export const AppHeader = ({ showBack = true, backTo = '/', syncStatus, completedSteps, title, teamMembers, onToggleTeam, onMembersChange, canManageTeam = true }: AppHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [showOverviewDialog, setShowOverviewDialog] = useState(false);
  
  const projectIdFromQuery = new URLSearchParams(location.search).get("projectId");
  const projectIdFromPath =
    /^\/project\/([^/]+)(?:\/|$)/.exec(location.pathname)?.[1] ?? null;
  const projectId = projectIdFromQuery ?? projectIdFromPath;

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectId ? getProjectWithRfi(projectId) : Promise.reject('No project ID'),
    enabled: !!projectId && showOverviewDialog,
  });

  const navigatePreservingProject = (path: string) => {
    if (!projectId) {
      navigate(path);
      return;
    }
    // Ensure we insert the query param before any hash fragment.
    const [beforeHash, hash = ""] = path.split("#", 2);
    const [base, query = ""] = beforeHash.split("?", 2);
    const params = new URLSearchParams(query);
    // If caller already provided a projectId, don't append another one.
    if (params.has("projectId")) {
      navigate(path);
      return;
    }
    params.set("projectId", projectId);
    const withQuery = `${base}?${params.toString()}`;
    navigate(hash ? `${withQuery}#${hash}` : withQuery);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <header style={{ height: 52, background: '#FFFFFF', borderBottom: '1px solid #E5E5E5', position: 'sticky', top: 0, zIndex: 10 }}>
      <div className="flex items-center justify-between h-full" style={{ padding: '0 32px' }}>
        {/* Left: Project info + Avatars */}
        <div className="flex items-center gap-3">
          {title && (
            <>
              {projectId ? (
                <button onClick={() => navigate(`/project/${projectId}`)} style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', letterSpacing: '-0.01em' }} className="hover:opacity-80 transition-opacity">
                  {title}
                </button>
              ) : (
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', letterSpacing: '-0.01em' }}>{title}</span>
              )}
              <span style={{ fontSize: 13, color: '#8A8A84', fontWeight: 450 }}>Financial Due Diligence</span>
            </>
          )}
          {teamMembers && teamMembers.length > 0 && (
            <>
              <div style={{ width: 1, height: 20, background: '#E5E5E5', margin: '0 4px' }} />
              <TeamAvatars members={teamMembers} onClick={onToggleTeam} onMembersChange={onMembersChange} canManage={canManageTeam} />
            </>
          )}
        </div>

        {/* Right: All controls */}
        <div className="flex items-center" style={{ gap: 8 }}>
          {syncStatus}

          {/* Back */}
          {showBack && location.pathname !== '/' && (
            <button
              onClick={() => navigatePreservingProject(backTo)}
              className="inline-flex items-center hover:bg-[#FAF9F6] transition-all"
              style={{ gap: 5, height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid #E5E5E5', background: '#FFFFFF', color: '#52524E', fontSize: 12.5, fontWeight: 500 }}
            >
              <ArrowLeft style={{ width: 14, height: 14 }} />
              Back
            </button>
          )}

          {/* Sign out */}
          <button
            onClick={handleSignOut}
            className="inline-flex items-center hover:bg-[#FAF9F6] transition-all"
            style={{ gap: 5, height: 32, padding: '0 12px', borderRadius: 6, border: 'none', background: 'none', color: '#8A8A84', fontSize: 12.5, fontWeight: 500 }}
          >
            <LogOut style={{ width: 14, height: 14 }} />
            Sign out
          </button>
        </div>
      </div>

      <Dialog open={showOverviewDialog} onOpenChange={setShowOverviewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Project Overview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Project Name
              </label>
              <div className="px-3 py-2 rounded-md border border-border/50 bg-muted/30 text-sm">
                {projectData?.name || '—'}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Entity Name
              </label>
              <div className="px-3 py-2 rounded-md border border-border/50 bg-muted/30 text-sm">
                {projectData?.entity_name || '—'}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Jurisdictions
              </label>
              <div className="px-3 py-2 rounded-md border border-border/50 bg-muted/30 text-sm">
                {projectData?.jurisdictions?.length
                  ? projectData.jurisdictions.join(', ')
                  : '—'}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Deal Type
              </label>
              <div className="px-3 py-2 rounded-md border border-border/50 bg-muted/30 text-sm">
                {projectData?.deal_type
                  ? projectData.deal_type === 'buy_side' ? 'Buy-side' : 'Sell-side'
                  : '—'}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
